<template>
  <b-modal
    :visible="value"
    :title="modalTitle"
    size="md"
    @hidden="handleHidden"
    @ok="handleSubmit"
  >
    <b-alert
      v-if="department && department.manager"
      variant="info"
      show
      class="mb-2"
    >
      <div class="alert-body">
        <strong>Current Manager:</strong>
        <br>
        {{ department.manager.full_name }} ({{ department.manager.employee_code }})
      </div>
    </b-alert>

    <b-form @submit.prevent="handleSubmit">
      <!-- Manager Selection -->
      <b-form-group
        label="Select New Manager"
        label-for="manager"
      >
        <v-select
          id="manager"
          v-model="selectedManagerId"
          :options="availableEmployees"
          :reduce="emp => emp.id"
          :get-option-label="getEmployeeLabel"
          placeholder="Select department manager"
          :loading="loadingEmployees"
          :disabled="loadingEmployees"
          :filterable="true"
        >
          <template #option="option">
            <div>
              <strong>{{ option.full_name }}</strong>
              <br>
              <small class="text-muted">{{ option.employee_code }}</small>
              <b-badge
                v-if="department && option.id === department.manager_id"
                variant="light-primary"
                class="ml-50"
              >
                Current
              </b-badge>
            </div>
          </template>
          <template #no-options="{ search, searching }">
            <template v-if="searching">
              No results found for <em>{{ search }}</em>.
            </template>
            <em v-else>Start typing to search...</em>
          </template>
        </v-select>
        <b-form-text>Only active (contracted) employees can be assigned as managers</b-form-text>
      </b-form-group>
    </b-form>

    <!-- Remove Manager Option -->
    <div
      v-if="department && department.manager"
      class="mt-2"
    >
      <hr>
      <b-button
        variant="outline-danger"
        size="sm"
        block
        @click="handleRemoveManager"
      >
        <feather-icon
          icon="XIcon"
          class="mr-50"
        />
        Remove Current Manager
      </b-button>
    </div>

    <template #modal-footer="{ ok, cancel }">
      <b-button
        variant="outline-secondary"
        @click="cancel()"
      >
        Cancel
      </b-button>
      <b-button
        variant="primary"
        :disabled="submitting || !selectedManagerId"
        @click="ok()"
      >
        <b-spinner
          v-if="submitting"
          small
          class="mr-50"
        />
        Assign Manager
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal,
  BForm,
  BFormGroup,
  BFormText,
  BButton,
  BSpinner,
  BAlert,
  BBadge,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { mapActions } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'ManagerAssignmentModal',

  components: {
    BModal,
    BForm,
    BFormGroup,
    BFormText,
    BButton,
    BSpinner,
    BAlert,
    BBadge,
    vSelect,
  },

  props: {
    value: {
      type: Boolean,
      required: true,
    },
    department: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      selectedManagerId: null,
      availableEmployees: [],
      loadingEmployees: false,
      submitting: false,
    }
  },

  computed: {
    modalTitle() {
      return this.department
        ? `Manage Manager - ${this.department.name}`
        : 'Assign Manager'
    },
  },

  watch: {
    value(val) {
      if (val) {
        this.selectedManagerId = this.department && this.department.manager_id ? this.department.manager_id : null
        this.loadEmployees()
      }
    },
  },

  methods: {
    ...mapActions('departments', ['assignManager', 'removeManager']),

    async loadEmployees() {
      this.loadingEmployees = true
      try {
        // Using employees API endpoint - adjust based on your actual API
        const response = await this.$axios.post('/employees/list', {
          per_page: 1000,

        })
        this.availableEmployees = response.data.data || []
      } catch (error) {
        console.error('Error loading employees:', error)
        this.availableEmployees = []
      } finally {
        this.loadingEmployees = false
      }
    },

    getEmployeeLabel(employee) {
      return `${employee.full_name} (${employee.employee_code})`
    },

    async handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()

      if (!this.selectedManagerId) {
        return
      }

      this.submitting = true
      try {
        await this.assignManager({
          departmentId: this.department.id,
          managerId: this.selectedManagerId,
        })

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            icon: 'CheckIcon',
            text: 'Manager assigned successfully',
            variant: 'success',
          },
        })

        this.$emit('assigned')
        this.handleHidden()
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            text: error.response?.data?.message || 'Failed to assign manager',
            variant: 'danger',
          },
        })
      } finally {
        this.submitting = false
      }
    },

    async handleRemoveManager() {
      const result = await this.$swal({
        title: 'Remove Manager?',
        text: 'Are you sure you want to remove the current manager from this department?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, remove!',
        cancelButtonText: 'Cancel',
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      })

      if (!result.value) {
        return
      }

      this.submitting = true
      try {
        await this.removeManager(this.department.id)

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            icon: 'CheckIcon',
            text: 'Manager removed successfully',
            variant: 'success',
          },
        })

        this.$emit('assigned')
        this.handleHidden()
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            text: error.response?.data?.message || 'Failed to remove manager',
            variant: 'danger',
          },
        })
      } finally {
        this.submitting = false
      }
    },

    handleHidden() {
      this.$emit('input', false)
      this.selectedManagerId = null
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@core/scss/vue/libs/vue-select.scss';
</style>
